import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AddButton,
  AlertIcon,
  Body,
  CardContent,
  CardMeta,
  CardTitle,
  CloseButton,
  Container,
  Deadline,
  DeadlineAlert,
  DeadlineDate,
  Description,
  HeaderInfo,
  MetaIcon,
  MetaItem,
  Modal,
  ModalContent,
  PaginationWrapper,
  PlusIcon,
  ViewCard,
  ViewCardContent,
  ViewList,
  Wrapper,
} from "../style/view.styles";
import Pagination from "../components/Elements/Pagination";
import { fetchEntityData } from "../utils/RequestHandler";
import EmptyState from "../components/Elements/EmptyState";
import LoadingSpinner from "../components/Elements/LoadingSpinner";
import FilterSidebar from "../components/Elements/FilterSidebar";
import { getLoggedInUser } from "../utils/AuthHandler";
import AddOpportunityForm from "../components/Elements/AddOpportunityForm";
import { FiClock, FiHome, FiCalendar } from "react-icons/fi";

const filters = [
  {
    filter: "Employment Type",
    options: ["Full-time", "Part-time", "Contract", "Internship", "Volunteer"],
  },
  {
    filter: "Work Mode",
    options: ["Remote", "On-site", "Hybrid", "Flexible"],
  },
];

export default function OpportunitiesPage() {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedOpportunity, setSelectedOpportunity] = useState(
    location.state?.opportunity || null
  );
  const [filteredOpportunities, setFilteredOpportunities] = useState([]);

  const [isLoggedIn, setIsLoggedIn] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [isProtected, setIsProtected] = useState(null);
  const [user, setUser] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const handleOpenAddModal = () => {
    setIsAddModalOpen(true);
  };

  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
  };

  const handleModalBackgroundClick = (e) => {
    if (e.target === e.currentTarget) {
      handleCloseAddModal();
    }
  };

  const navigate = useNavigate();

  const recordsPerPage = 5;
  useEffect(() => {
    const checkUserLoggedIn = async () => {
      const user = getLoggedInUser();
      if (user) {
        const role = user.role;
        setIsLoggedIn(true);
        setUserRole(role);
        setUser(user);
        setIsProtected(role === "organization");
      } else {
        setIsLoggedIn(false);
        setUserRole(null);
        setIsProtected(false);
      }
    };

    checkUserLoggedIn();
  }, []);

  useEffect(() => {
    if (isLoggedIn !== null && isProtected !== null) {
      const fetchOpportunities = async () => {
        try {
          setLoading(true);
          const data = await fetchEntityData("opportunities", isProtected);
          if (data.success) {
            const records = data.result;
            setFilteredOpportunities(records);
          }
        } catch (error) {
          console.error("Failed to fetch opportunities:", error);
        } finally {
          setLoading(false);
        }
      };
      fetchOpportunities();
    }
  }, [isProtected, isLoggedIn, userRole]);

  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredOpportunities.slice(
    indexOfFirstRecord,
    indexOfLastRecord
  );

  const handlePageChange = (page) => setCurrentPage(page);

  const handleSelectOpportunity = (opportunity) => {
    setSelectedOpportunity(opportunity);
    navigate("/opportunities-detail", {
      state: { opportunity },
    });
  };

  function isDeadlineApproaching(deadlineDate) {
    const deadline = new Date(deadlineDate);
    const today = new Date();
    const differenceInDays = Math.ceil(
      (deadline - today) / (1000 * 60 * 60 * 24)
    );

    return differenceInDays >= 0 && differenceInDays <= 7;
  }

  return (
    <>
      <Wrapper className="paleBlueGreyBg">
        <Container>
          {/* Sidebar Section */}
          <FilterSidebar filters={filters} />

          {/* Body Section */}
          <Body>
            <HeaderInfo>
              <h1 className="font30 darkGreenColor extraBold">
                Available Opportunities
              </h1>
              {isLoggedIn && user?.role === "organization" && (
                <AddButton onClick={handleOpenAddModal}>
                  <PlusIcon />
                  <span>Add New</span>
                </AddButton>
              )}
            </HeaderInfo>
            {loading && <LoadingSpinner />}
            {!loading && currentRecords?.length === 0 && <EmptyState />}
            {!loading && currentRecords?.length > 0 && (
              <ViewList>
                {currentRecords.map((record, index) => (
                  <ViewCard
                    key={index}
                    isSelected={selectedOpportunity === record}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectOpportunity(record);
                    }}
                  >
                    <ViewCardContent>
                      <CardContent>
                        <CardTitle>{record.title}</CardTitle>

                        <Description>
                          {`${record.description.slice(0, 50)}${
                            record.description.length > 50 ? "..." : ""
                          }`}
                        </Description>

                        <CardMeta>
                          <MetaItem>
                            <MetaIcon>
                              <FiHome />
                            </MetaIcon>
                            <span>{record.organization}</span>
                          </MetaItem>

                          <Deadline>
                            <MetaIcon>
                              <FiCalendar />
                            </MetaIcon>
                            <span>Deadline: </span>
                            <DeadlineDate>
                              {new Date(
                                record.application_deadline
                              ).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              })}
                            </DeadlineDate>

                            {isDeadlineApproaching(
                              record.application_deadline
                            ) && (
                              <DeadlineAlert>
                                <AlertIcon>
                                  <FiClock />
                                </AlertIcon>
                                Closing soon
                              </DeadlineAlert>
                            )}
                          </Deadline>
                        </CardMeta>
                      </CardContent>
                    </ViewCardContent>
                  </ViewCard>
                ))}
              </ViewList>
            )}
            {filteredOpportunities.length > recordsPerPage && (
              <PaginationWrapper>
                <Pagination
                  totalItems={filteredOpportunities.length}
                  itemsPerPage={recordsPerPage}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                />
              </PaginationWrapper>
            )}
          </Body>
        </Container>
      </Wrapper>
      {isAddModalOpen && (
        <Modal onClick={handleModalBackgroundClick}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <CloseButton></CloseButton>
            <AddOpportunityForm
              handleModalClose={handleCloseAddModal}
              onSuccess={() => {
                // Reload opportunities after successful creation
                handleCloseAddModal();
                setLoading(true);
                fetchEntityData("opportunities", isProtected).then((data) => {
                  if (data.success) {
                    setFilteredOpportunities(data.result);
                  }
                  setLoading(false);
                });
              }}
            />
          </ModalContent>
        </Modal>
      )}
    </>
  );
}
